// CheckSupplierOrganisation.tsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { supabase } from '../../lib/supabase';
import type { DropdownSelectOption, UserOrganisation } from '../../types/types';
import DropdownSelect from '../common/DropdownSelect';
import { IndustrySelect } from '../common/IndustrySelect';
import { countryList } from '../../const/const';
import { captureSupabaseError } from '../../lib/sentry';
import { useLogout } from '../../hooks/useAuth';

interface Props {
  show: boolean;
  onHide: () => void;
  selectedOrganisation: UserOrganisation | null;
  onUpdate?: (updatedOrg: UserOrganisation) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getErrorMessage = (err: any): string => {
  // Check for Postgres Unique Constraint Violation
  if (
    err.code === '23505' ||
    err.message?.includes('organisations_registration_number_key')
  ) {
    return 'This registration number is already registered. If your company already has an account, please contact your administrator or support: info@supplypassport.co';
  }
  if (err.status === 401) {
    return 'Your session has expired. Please refresh the page and log in again before saving.';
  }
  return 'An unexpected error occurred. Please try again.';
};

const getEdgeFunctionMessage = async (response?: Response | null) => {
  if (!response) {
    return null;
  }

  try {
    const text = await response.text();

    if (!text) {
      return null;
    }

    try {
      const json = JSON.parse(text);
      return json.error || json.message || text;
    } catch {
      return text;
    }
  } catch {
    return null;
  }
};

const CheckSupplierOrganisation: React.FC<Props> = ({
  show,
  onHide,
  selectedOrganisation,
  onUpdate,
}) => {
  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [regNumber, setRegNumber] = useState('');
  const [country, setCountry] = useState('');
  const [industry, setIndustry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const logout = useLogout();

  // Field validation state
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    legalName: '',
    regNumber: '',
    country: '',
    industry: '',
  });

  // Pre-fill fields when organisation changes
  useEffect(() => {
    if (selectedOrganisation) {
      setName(selectedOrganisation.organisation?.name || '');
      setLegalName(selectedOrganisation.organisation?.legal_name || '');
      setCountry(selectedOrganisation.organisation?.country || '');
      setRegNumber(
        selectedOrganisation.organisation?.registration_number || '',
      );
      setIndustry('');
      setError('');
      setFieldErrors({
        name: '',
        legalName: '',
        regNumber: '',
        country: '',
        industry: '',
      });
    }
  }, [selectedOrganisation]);

  // if (
  //   (!selectedOrganisation ||
  //     selectedOrganisation.organisation?.status !== 'pending_confirmation') &&
  //   selectedOrganisation?.organisation?.name !== null
  // ) {
  //   return null; // Don't show modal if no organisation or not pending
  // }

  if (!selectedOrganisation) {
    return null; // Don't show modal if no organisation is selected
  }

  const validateFields = (): boolean => {
    const errors: typeof fieldErrors = {
      name: '',
      legalName: '',
      regNumber: '',
      country: '',
      industry: '',
    };
    let valid = true;

    if (!name.trim()) {
      errors.name = 'Organisation Name is required';
      valid = false;
    }
    if (!regNumber.trim()) {
      errors.regNumber = 'Registration Number is required';
      valid = false;
    }
    if (!country) {
      errors.country = 'Country is required';
      valid = false;
    }
    if (!industry) {
      errors.industry = 'Industry is required';
      valid = false;
    }

    setFieldErrors(errors);
    return valid;
  };

  const handleSave = async () => {
    if (!validateFields()) return;

    setLoading(true);
    setError('');

    try {
      // functions.invoke() silently falls back to the anon key (-> 401) if there's no
      // live session, e.g. after a backgrounded mobile tab drops its token; refresh first.
      let { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        ({ data: sessionData } = await supabase.auth.refreshSession());
      }
      if (!sessionData.session) {
        throw Object.assign(
          new Error(
            'Your session has expired. Please refresh the page and log in again.',
          ),
          { status: 401 },
        );
      }

      const { error: updateError, response } = await supabase.functions.invoke(
        'update-supplier-organisation',
        {
          body: {
            organisationId: selectedOrganisation.organisation?.id,
            name,
            legalName,
            registrationNumber: regNumber,
            country,
            industryId: industry,
          },
        },
      );

      if (updateError) {
        const message = await getEdgeFunctionMessage(response);
        throw Object.assign(new Error(message || updateError.message), {
          code: updateError.name,
          status: response?.status,
        });
      }

      const { data: updatedOrg, error: fetchError } = await supabase
        .from('organisations')
        .select('*')
        .eq('id', selectedOrganisation.organisation?.id)
        .single();

      if (fetchError) throw fetchError;

      onUpdate?.({
        ...selectedOrganisation,
        organisation: {
          ...selectedOrganisation.organisation,
          ...updatedOrg,
        },
      });
      onHide?.();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      captureSupabaseError(err, {
        op: 'organisation.update',
        status: err?.status,
        code: err?.code,
      });
      console.error('Error updating organisation:', err);

      // Update state with the specific error message
      const friendlyMessage = getErrorMessage(err);
      setError(friendlyMessage);

      // Optionally highlight the specific field
      if (err.code === '23505') {
        setFieldErrors((prev) => ({
          ...prev,
          regNumber: 'This number is already in use.',
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const countryOptions: DropdownSelectOption[] = countryList.map((c) => ({
    id: c.isoCode,
    label: c.countryName,
  }));

  const handleCountrySelected = (
    option: DropdownSelectOption | DropdownSelectOption[],
  ) => {
    setCountry((option as DropdownSelectOption).id);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Welcome to SupplyPassport!</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h5 className="text-secondary mb-3">
          Please confirm your organisation details
        </h5>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form>
          <Form.Group className="mb-3" controlId="orgName">
            <Form.Label>Organisation Name</Form.Label>
            <Form.Control
              type="text"
              value={name}
              isInvalid={!!fieldErrors.name}
              onChange={(e) => setName(e.target.value)}
            />
            <Form.Control.Feedback type="invalid">
              {fieldErrors.name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="legalName">
            <Form.Label>Legal Name</Form.Label>
            <Form.Control
              type="text"
              value={legalName}
              isInvalid={!!fieldErrors.legalName}
              onChange={(e) => setLegalName(e.target.value)}
            />
            <Form.Control.Feedback type="invalid">
              {fieldErrors.legalName}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="regNumber">
            <Form.Label>Registration Number</Form.Label>
            <Form.Control
              type="text"
              value={regNumber}
              isInvalid={!!fieldErrors.regNumber}
              onChange={(e) => setRegNumber(e.target.value)}
            />
            <Form.Control.Feedback type="invalid">
              {fieldErrors.regNumber}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="country">
            <Form.Label>Country</Form.Label>
            <DropdownSelect
              full
              options={countryOptions}
              selectedIds={country}
              onValueChanged={handleCountrySelected}
            />
            {fieldErrors.country && (
              <div className="text-danger small mt-1">
                {fieldErrors.country}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3" controlId="industry">
            <Form.Label>Industry</Form.Label>
            <IndustrySelect
              full
              value={industry}
              isInvalid={!!fieldErrors.industry}
              onChange={(val) => setIndustry(String(val ?? ''))}
            />
            {fieldErrors.industry && (
              <div className="text-danger small mt-1">
                {fieldErrors.industry}
              </div>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="link" onClick={() => logout()}>
          Logout
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            'Confirm & Save'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CheckSupplierOrganisation;
